import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { DealerActionTimeout, StoreTermsAndConditions, CustomWebhookState } from '@/model/profile.mjs';
import { CreateUserInterface, EditUserInterface, PreferredDealersMetaData, StoreConfigForm, StoreFilterConfig } from '../types';

interface InitialStateInterface {
  is_pending_workflow: boolean | null;
  is_multi_tenant?: boolean | null;
  isCreatingUser: boolean;
  isUserCreated: boolean;
  isDeletingUser: boolean;
  isUserDelete: boolean;
  createUserEmailError: string;
  isSavingEditUserDetails: boolean;
  isEditUserDetailsSaved: boolean;
  isGneratingEncryptionKey: boolean;
  isEncryptionKeyGenerated: boolean;
  isRemovingEncryptionKey: boolean;
  storeTermsAndConditions: StoreTermsAndConditions;
  updatedStoreTermsAndConditions: StoreTermsAndConditions
  isEncryptionKeyRemoved: boolean;
  isUpdatingStoreConfiguration: boolean;
  isUpdatingDealerctionTimeout: boolean;
  isUpdatingStoreFilterConfig: boolean;
  isStoreConfigurationUpdated: boolean;
  isStoreFilterConfigChanged: boolean;
  isUpdatingFilterConfig: boolean;
  fflStoreFilterConfig: StoreFilterConfig;
  updatedStoreFilterConfig: StoreFilterConfig;
  isTermsAndConditionsUpdated: boolean
  isTermsAndConditionsChanged: boolean
  isEditingTermsAndConditionEnabled: boolean,
  isLoadingPreferredDealersExcelGeneration: boolean,
  isExportPreferredDealersButtonDisabled: boolean,
  preferredDealersMetaData: any,
  needToCallPreferredDealersAPI: boolean,
  customWebhookState: CustomWebhookState,
  isSavingEcommerceStatusMapping: boolean;
  ecommerceStatusMappingSaveError: string | null;
  isDeletingEcommerceStatusMapping: boolean;
  ecommerceStatusMappingDeleteError: string | null;
  ecommerceStatusList: string[];
  isLoadingEcommerceStatusList: boolean;
  ecommerceStatusListLoadError: string | null;
}
export const storeTermsAndConditionInitailValue = {
  terms_and_conditions: ''
}
export const customWebhookInitialState = {
  data: {
    id: null,
    url: '',
    secret: '',
    is_enabled: false,
    send_webhook_for_linked_transfers_standalone: false,
    send_webhook_for_linked_transfers_ecommerce: false,
    linked_transfer_webhook_delay_minutes: 0,
  },
  isLoadingSave: false,
  isPostDataSave: false,
  isLoadingTestConnection: false,
  isLoadingRotate: false,
  error: null,
  testConnectionResult: null,
  rotateResult: null
};

export const fflStoreFilterConfigInitialValue = { filter_config: {} }
const initialState: InitialStateInterface = {
  is_pending_workflow: null,
  isCreatingUser: false,
  isUserCreated: false,
  isDeletingUser: false,
  isUserDelete: false,
  createUserEmailError: "",
  isSavingEditUserDetails: false,
  isEditUserDetailsSaved: false,
  isGneratingEncryptionKey: false,
  isEncryptionKeyGenerated: false,
  isRemovingEncryptionKey: false,
  isEncryptionKeyRemoved: false,
  isUpdatingStoreConfiguration: false,
  isUpdatingDealerctionTimeout: false,
  isUpdatingStoreFilterConfig: false,
  isStoreConfigurationUpdated: false,
  isStoreFilterConfigChanged: false,
  isUpdatingFilterConfig: false,
  fflStoreFilterConfig: fflStoreFilterConfigInitialValue,
  storeTermsAndConditions: storeTermsAndConditionInitailValue,
  updatedStoreTermsAndConditions: storeTermsAndConditionInitailValue,
  updatedStoreFilterConfig: fflStoreFilterConfigInitialValue,
  isTermsAndConditionsUpdated: false,
  isTermsAndConditionsChanged: false,
  isEditingTermsAndConditionEnabled: false,
  isLoadingPreferredDealersExcelGeneration: false,
  isExportPreferredDealersButtonDisabled: false,
  preferredDealersMetaData: {},
  needToCallPreferredDealersAPI: false,
  customWebhookState: customWebhookInitialState,
  isSavingEcommerceStatusMapping: false,
  ecommerceStatusMappingSaveError: null,
  isDeletingEcommerceStatusMapping: false,
  ecommerceStatusMappingDeleteError: null,
  ecommerceStatusList: [],
  isLoadingEcommerceStatusList: false,
  ecommerceStatusListLoadError: null
};

export const StoreAdminSlice = createSlice({
  name: 'StoreAdminSlice',
  initialState,
  reducers: {
    setAutomateTransferWorkflow: (state, action: PayloadAction<boolean>) => {
      state.is_pending_workflow = action.payload;
    },
    setIsMultiTenantStore: (state, action: PayloadAction<boolean>) => {
      state.is_multi_tenant = action.payload
    },
    setIsCreatingUser: (state, action: PayloadAction<boolean>) => {
      state.isCreatingUser = action.payload;
    },
    setIsUserCreated: (state, action: PayloadAction<boolean>) => {
      state.isUserCreated = action.payload;
    },
    setIsDeletingUser: (state, action: PayloadAction<boolean>) => {
      state.isDeletingUser = action.payload;
    },
    setIsUserDelete: (state, action: PayloadAction<boolean>) => {
      state.isUserDelete = action.payload;
    },
    setCreateUserEmailError: (state, action: PayloadAction<string>) => {
      state.createUserEmailError = action.payload;
    },
    setIsEditUserDetailsSaved: (state, action: PayloadAction<boolean>) => {
      state.isEditUserDetailsSaved = action.payload;
    },
    setIsSavingEditUserDetails: (state, action: PayloadAction<boolean>) => {
      state.isSavingEditUserDetails = action.payload;
    },
    setisEncryptionKeyGenerated: (state, action: PayloadAction<boolean>) => {
      state.isEncryptionKeyGenerated = action.payload;
    },
    setIsGneratingEncryptionKey: (state, action: PayloadAction<boolean>) => {
      state.isGneratingEncryptionKey = action.payload;
    },
    setIsRemovingEncryptionKey: (state, action: PayloadAction<boolean>) => {
      state.isRemovingEncryptionKey = action.payload;
    },
    setIsEncryptionKeyRemoved: (state, action: PayloadAction<boolean>) => {
      state.isEncryptionKeyRemoved = action.payload;
    },
    setIsUpdatingStoreConfiguration: (state, action: PayloadAction<boolean>) => {
      state.isUpdatingStoreConfiguration = action.payload;
    },
    setIsUpdatingDealerctionTimeout:  (state, action: PayloadAction<boolean>) => {
      state.isUpdatingDealerctionTimeout = action.payload;
    },
    setIsUpdatingStoreFilterConfig: (state, action: PayloadAction<boolean>) => {
      state.isUpdatingStoreFilterConfig = action.payload;
    },
    setIsStoreConfigurationUpdated: (state, action: PayloadAction<boolean>) => {
      state.isStoreConfigurationUpdated = action.payload;
    },
    setIsUpdatingFilterConfig: (state, action: PayloadAction<boolean>) => {
        state.isUpdatingFilterConfig = action.payload;
    },
    setIsStoreFilterConfigChanged: (state, action: PayloadAction<boolean>) => {
      state.isStoreFilterConfigChanged = action.payload;
    },
    setStoreFilterConfig: (state, action: PayloadAction<StoreFilterConfig>) => {
      state.fflStoreFilterConfig = action.payload;
    },
    setUpdatedStoreFilterConfig: (state, action: PayloadAction<StoreFilterConfig>) => {
      state.updatedStoreFilterConfig = action.payload;
    },
    setIsLoadingPreferredDealersExcelGeneration: (state, action: PayloadAction<boolean>) => {
      state.isLoadingPreferredDealersExcelGeneration = action.payload;
    },
    setIsExportPreferredDealersButtonDisabled: (state, action: PayloadAction<boolean>) => {
      state.isExportPreferredDealersButtonDisabled = action.payload;
    },
    setPreferredDealersMetaData: (state, action: PayloadAction<PreferredDealersMetaData>) => {
      state.preferredDealersMetaData = action.payload;
    },
    setPreferredDealersBEAPICall: (state, action: PayloadAction<boolean>) => {
      state.needToCallPreferredDealersAPI = action.payload;
    },
    setCustomWebhookData: (state, action: PayloadAction<any>) => {
      state.customWebhookState.data = action.payload;
    },
    setCustomWebhookIsLoadingSave: (state, action: PayloadAction<boolean>) => {
      state.customWebhookState.isLoadingSave = action.payload;
    },
    setCustomWebhookIsPostDataSave: (state, action: PayloadAction<boolean>) => {
      state.customWebhookState.isPostDataSave = action.payload;
    },
    setCustomWebhookIsLoadingTestConnection: (state, action: PayloadAction<boolean>) => {
      state.customWebhookState.isLoadingTestConnection = action.payload;
    },
    setCustomWebhookIsLoadingRotate: (state, action: PayloadAction<boolean>) => {
      state.customWebhookState.isLoadingRotate = action.payload;
    },
    setCustomWebhookError: (state, action: PayloadAction<string | null>) => {
      state.customWebhookState.error = action.payload;
    },
    setCustomWebhookTestResult: (state, action: PayloadAction<any>) => {
      state.customWebhookState.testConnectionResult = action.payload;
    },
    setCustomWebhookRotateResult: (state, action: PayloadAction<any>) => {
      state.customWebhookState.rotateResult = action.payload;
    },
    clearCustomWebhookError: (state) => {
      state.customWebhookState.error = null;
    },
    clearCustomWebhookTestResult: (state) => {
      state.customWebhookState.testConnectionResult = null;
    },
    // saga
    callToDeleteUser: (state, action: PayloadAction<string>) => { },
    callToUpdateAutomateTransferWorkflow: () => { },
    callToCreateUser: (state, action: PayloadAction<CreateUserInterface>) => { },
    callToEditUser: (state, action: PayloadAction<{ data: EditUserInterface, userId: string }>) => { },
    callToGenerateEncryptionKey: () => { },
    callToRemoveEncryptionKey: () => { },
    callToUpdateStoreConfiguration: (state, action: PayloadAction<Partial<StoreConfigForm>>) => { },
    callToUpdateDealerActionTimeout: (state, action:PayloadAction<Partial<DealerActionTimeout>>) => { },
    callToUpdateStoreFilterConfig: (state, action: PayloadAction<Partial<StoreFilterConfig>>) => { },
    setStoreTermsAndConditions: (state, action: PayloadAction<StoreTermsAndConditions>) => {
      state.storeTermsAndConditions = action.payload;
    },
    callToGetStoreTermsAndConditions: () => {},
    callToGetCustomWebhookTestConnectionResponse: (state, action: PayloadAction<{webhookUrl: string}>) => {},
    callToGetCustomWebhookRotateResponse: (state, action: PayloadAction<{webhookUrl: string}>) => {},
    callToUpdateCustomWebhookConfiguration: (state, action: PayloadAction<{ is_enabled?: boolean, url?: string, secret?: string, send_webhook_for_linked_transfers_standalone?: boolean, send_webhook_for_linked_transfers_ecommerce?: boolean, linked_transfer_webhook_delay_minutes?: number }>) => {},
    callToGetCustomWebhookConfigurationResponse: () => {},
    setUpdatedStoreTermsAndCondition: (state, action: PayloadAction<StoreTermsAndConditions>) => {
      state.updatedStoreTermsAndConditions = action.payload;
    },
    callToUpdateStoreTermsAndConditions:() => {},
    setIsTermsAndCondtionUpdated: (state, action: PayloadAction<boolean>) => {
      state.isTermsAndConditionsUpdated = action.payload
    },
    setIsTermsAndCondtionChanged: (state, action: PayloadAction<boolean>) => {
      state.isTermsAndConditionsChanged = action.payload
    },
    setIsEditableTermsAndContionsEnabled: (state, action: PayloadAction<boolean>) => {
      state.isEditingTermsAndConditionEnabled = action.payload
    },
    downloadPreferredDealersListForExcel: () => {},
    setIsSavingEcommerceStatusMapping: (state, action: PayloadAction<boolean>) => {
      state.isSavingEcommerceStatusMapping = action.payload;
    },
    setEcommerceStatusMappingSaveError: (state, action: PayloadAction<string | null>) => {
      state.ecommerceStatusMappingSaveError = action.payload;
    },
    callToSaveEcommerceStatusMapping: (
      _state,
      _action: PayloadAction<{
        mode: 'add' | 'edit';
        ecommerce_status: string;
        action: string;
        description: string;
        id?: string | number;
      }>
    ) => {},
    setIsDeletingEcommerceStatusMapping: (state, action: PayloadAction<boolean>) => {
      state.isDeletingEcommerceStatusMapping = action.payload;
    },
    setEcommerceStatusMappingDeleteError: (state, action: PayloadAction<string | null>) => {
      state.ecommerceStatusMappingDeleteError = action.payload;
    },
    callToDeleteEcommerceStatusMapping: (
      _state,
      _action: PayloadAction<{ id: string | number }>
    ) => {},
    setEcommerceStatusList: (state, action: PayloadAction<string[]>) => {
      state.ecommerceStatusList = action.payload;
    },
    setIsLoadingEcommerceStatusList: (state, action: PayloadAction<boolean>) => {
      state.isLoadingEcommerceStatusList = action.payload;
    },
    setEcommerceStatusListLoadError: (state, action: PayloadAction<string | null>) => {
      state.ecommerceStatusListLoadError = action.payload;
    },
    callToFetchEcommerceStatusList: () => {}
  }
});

export const {
  setAutomateTransferWorkflow,
  setIsMultiTenantStore,
  setIsUserCreated,
  setIsCreatingUser,
  setIsDeletingUser,
  setIsUserDelete,
  setCreateUserEmailError,
  setIsSavingEditUserDetails,
  setIsEditUserDetailsSaved,
  setisEncryptionKeyGenerated,
  setIsGneratingEncryptionKey,
  setIsEncryptionKeyRemoved,
  setIsRemovingEncryptionKey,
  setIsStoreConfigurationUpdated,
  setIsUpdatingFilterConfig,
  setIsUpdatingStoreConfiguration,
  setIsUpdatingDealerctionTimeout,
  setIsUpdatingStoreFilterConfig,
  setIsStoreFilterConfigChanged,
  setStoreFilterConfig,
  setUpdatedStoreFilterConfig,
  setIsLoadingPreferredDealersExcelGeneration,
  setIsExportPreferredDealersButtonDisabled,
  setPreferredDealersMetaData,
  setPreferredDealersBEAPICall,
  // saga
  callToUpdateAutomateTransferWorkflow,
  callToCreateUser,
  callToDeleteUser,
  callToEditUser,
  callToGenerateEncryptionKey,
  callToRemoveEncryptionKey,
  callToUpdateStoreConfiguration,
  callToUpdateDealerActionTimeout,
  callToUpdateStoreFilterConfig,
  setStoreTermsAndConditions,
  callToGetStoreTermsAndConditions,
  callToGetCustomWebhookTestConnectionResponse,
  callToGetCustomWebhookRotateResponse,
  callToUpdateCustomWebhookConfiguration,
  callToGetCustomWebhookConfigurationResponse,
  setUpdatedStoreTermsAndCondition,
  callToUpdateStoreTermsAndConditions,
  setIsTermsAndCondtionUpdated,
  setIsTermsAndCondtionChanged,
  setIsEditableTermsAndContionsEnabled,
  downloadPreferredDealersListForExcel,
  setCustomWebhookData,
  setCustomWebhookIsLoadingSave,
  setCustomWebhookIsPostDataSave,
  setCustomWebhookIsLoadingTestConnection,
  setCustomWebhookIsLoadingRotate,
  setCustomWebhookError,
  setCustomWebhookTestResult,
  setCustomWebhookRotateResult,
  clearCustomWebhookError,
  clearCustomWebhookTestResult,
  setIsSavingEcommerceStatusMapping,
  setEcommerceStatusMappingSaveError,
  callToSaveEcommerceStatusMapping,
  setIsDeletingEcommerceStatusMapping,
  setEcommerceStatusMappingDeleteError,
  callToDeleteEcommerceStatusMapping,
  setEcommerceStatusList,
  setIsLoadingEcommerceStatusList,
  setEcommerceStatusListLoadError,
  callToFetchEcommerceStatusList,
} = StoreAdminSlice.actions;

export default StoreAdminSlice.reducer;
